import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const transparentBg = isHome && !isScrolled;

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/hakkimizda', label: t('nav.about') },
    { path: '/faaliyetlerimiz', label: t('nav.activities') },
    { path: '/uygulamalarimiz', label: t('nav.applications') },
    { path: '/referanslarimiz', label: t('nav.references') },
    { path: '/cozum-ortaklarimiz', label: t('nav.partners') },
    { path: '/iletisim', label: t('nav.contact') },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparentBg
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-xl border-b border-foreground-100/40'
      }`}
    >
      {/* Top bar */}
      <div
        className={`hidden lg:block transition-all duration-500 overflow-hidden ${
          transparentBg ? 'border-b border-background-50/15 h-10' : 'border-b border-foreground-100/30 h-10'
        }`}
      >
        <div className="container-page h-full flex items-center justify-between">
          <div className="flex items-center gap-8">
            <a
              href="tel:+902122216628"
              className={`flex items-center gap-2 text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                transparentBg ? 'text-background-50/70 hover:text-background-50' : 'text-foreground-500 hover:text-primary-600'
              }`}
            >
              <i className="ri-phone-line w-3.5 h-3.5 flex items-center justify-center"></i>
              +90 212 221 66 28
            </a>
            <a
              href="tel:+902122216638"
              className={`flex items-center gap-2 text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                transparentBg ? 'text-background-50/70 hover:text-background-50' : 'text-foreground-500 hover:text-primary-600'
              }`}
            >
              <i className="ri-phone-line w-3.5 h-3.5 flex items-center justify-center"></i>
              +90 212 221 66 38
            </a>
            <a
              href="mailto:info@erdemirelektrik.com.tr"
              className={`flex items-center gap-2 text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                transparentBg ? 'text-background-50/70 hover:text-background-50' : 'text-foreground-500 hover:text-primary-600'
              }`}
            >
              <i className="ri-mail-line w-3.5 h-3.5 flex items-center justify-center"></i>
              info@erdemirelektrik.com.tr
            </a>
          </div>
          <div className="flex items-center gap-6">
            <span className={`text-xs ${transparentBg ? 'text-background-50/50' : 'text-foreground-400'}`}>
              Perpa Ticaret Merkezi A Blok K:10/11 No:1375 Şişli / İSTANBUL
            </span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className={`container-page flex items-center justify-between transition-all duration-500 ${
        transparentBg ? 'h-20' : 'h-[72px]'
      }`}>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-4 group flex-shrink-0">
          <div className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-500 cursor-pointer ${
            transparentBg ? 'bg-background-50/15 backdrop-blur-sm' : 'bg-primary-500'
          }`}>
            <i className={`ri-flashlight-fill text-2xl transition-colors duration-500 ${
              transparentBg ? 'text-background-50' : 'text-background-50'
            }`}></i>
          </div>
          <div className="hidden sm:flex flex-col">
            <span className={`text-base font-extrabold leading-none tracking-tight whitespace-nowrap transition-colors duration-500 ${
              transparentBg ? 'text-background-50' : 'text-foreground-950'
            }`}>
              ERDEMİR
            </span>
            <span className={`text-[11px] font-medium leading-tight tracking-widest uppercase whitespace-nowrap transition-colors duration-500 ${
              transparentBg ? 'text-background-50/70' : 'text-foreground-400'
            }`}>
              ELEKTRİK
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-0">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`relative px-4 py-2.5 text-[13px] font-medium rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer ${
                isActive(item.path)
                  ? transparentBg
                    ? 'text-background-50 bg-background-50/15'
                    : 'text-primary-600 bg-primary-50/80'
                  : transparentBg
                    ? 'text-background-50/75 hover:text-background-50 hover:bg-background-50/10'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-foreground-50'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
          <Link
            to="/iletisim"
            className={`px-5 py-2.5 text-[13px] font-semibold rounded-lg transition-all duration-500 whitespace-nowrap cursor-pointer flex items-center gap-2 ${
              transparentBg
                ? 'bg-background-50 text-foreground-950 hover:bg-background-100 hover:shadow-lg hover:shadow-foreground-950/10'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600 hover:shadow-lg hover:shadow-primary-500/25'
            }`}
          >
            <span>Teklif Alın</span>
            <i className="ri-arrow-right-up-line text-base w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer ${
            transparentBg
              ? 'text-background-50 hover:bg-background-50/15'
              : 'text-foreground-800 hover:bg-foreground-50'
          }`}
          aria-label="Menü"
        >
          <i className={`text-2xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} w-6 h-6 flex items-center justify-center`}></i>
        </button>
      </nav>

      {/* Mobile menu panel */}
      <div
        className={`lg:hidden transition-all duration-400 overflow-hidden ${
          isMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-background-50 border-t border-foreground-100/30">
          <div className="container-page py-6 flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-3.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                  isActive(item.path)
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-foreground-50'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-4 pt-4 border-t border-foreground-100/30 flex flex-col gap-3">
              <a href="tel:+902122216628" className="flex items-center gap-3 px-4 py-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                +90 212 221 66 28
              </a>
              <a href="mailto:info@erdemirelektrik.com.tr" className="flex items-center gap-3 px-4 py-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                info@erdemirelektrik.com.tr
              </a>
              <Link
                to="/iletisim"
                className="mx-4 mt-1 text-center px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all"
              >
                Teklif Alın
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}